/**
 * Per-brand feature flags.
 *
 * Add a brand's lowercase key to a list below to turn that feature on for it.
 */

// Brands that ask the "Is it a branded or designer jewelry piece?" question
// and route those items to specialist review instead of an instant estimate.
export const DESIGNER_REVIEW_BRANDS: string[] = ["maxpawn"];

// Brands whose Instant Quote flow skips the top-level category screen
// (Electronics vs Jewelry) entirely and starts directly on the Jewelry
// child-category screen (Gold / Silver / Platinum), since Jewelry is the
// only category they offer.
export const JEWELRY_ONLY_BRANDS: string[] = ["maxpawn"];

/**
 * Widget brand -> store-locator brand-name prefixes that count as that
 * brand's own store locations. Prefixes are matched case-insensitively
 * against the part of a store's `brandName` before " (" (see
 * `validateZipAndGetStores` in ez-quote.helpers.ts).
 *
 * Used to prioritize a brand's own stores in the "Visit a store" locator
 * before falling back to the nearest store of any brand.
 */
export const STORE_LOCATOR_BRAND_MAP: Record<string, string[]> = {
  maxpawn: ["max pawn luxury"],
};

// Radius (miles) within which a brand's own stores are prioritized in the
// "Visit a store" locator before falling back to nearest-any-brand.
export const STORE_LOCATOR_PRIORITY_RADIUS_MILES = 50;

/**
 * Whether this brand asks the branded/designer question and routes those
 * items to specialist review.
 * @param brand - Brand name (case-insensitive)
 */
export const isDesignerReviewBrand = (brand?: string | null): boolean => {
  if (!brand) return false;
  return DESIGNER_REVIEW_BRANDS.includes(brand.toLowerCase());
};

/**
 * Store-locator brand-name prefixes that should be treated as this widget
 * brand's own stores. Returns an empty list for brands with no dedicated
 * store-locator priority (falls back to default nearest-store behavior).
 * @param brand - Widget brand (case-insensitive)
 */
export const getOwnStoreBrandPrefixes = (brand?: string | null): string[] => {
  if (!brand) return [];
  return STORE_LOCATOR_BRAND_MAP[brand.toLowerCase()] ?? [];
};

/**
 * Whether this brand's Instant Quote flow should skip straight to the
 * Jewelry child-category screen (Gold / Silver / Platinum), bypassing the
 * top-level category selection screen.
 * @param brand - Brand name (case-insensitive)
 */
export const isJewelryOnlyBrand = (brand?: string | null): boolean => {
  if (!brand) return false;
  return JEWELRY_ONLY_BRANDS.includes(brand.toLowerCase());
};
